import { useEffect, useState } from 'react';
import { adminApi, formatDate } from '../api';

interface Alert {
  id: string;
  severity: 'warning' | 'critical';
  title: string;
  detail: string;
  clusterId: string | null;
  status: 'open' | 'resolved';
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  notifyCount: number;
}

/** Open alerts first; resolved ones stay visible for context. Evaluated every 15 minutes by maintenance. */
export function AlertsSection({ tick }: { tick: number }) {
  const [data, setData] = useState<Alert[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    adminApi<{ alerts: Alert[] }>('/alerts')
      .then((d) => setData(d.alerts))
      .catch((e: Error) => setError(e.message));
  }, [tick]);
  if (error) return <p className="error">{error}</p>;
  if (!data) return null;
  const open = data.filter((a) => a.status === 'open');
  const resolved = data.filter((a) => a.status === 'resolved').slice(0, 5);
  return (
    <div className={`card ${open.some((a) => a.severity === 'critical') ? 'alert-critical' : ''}`}>
      <div className="row between">
        <h2>
          Alerts{' '}
          {open.length === 0 ? (
            <span className="badge active">all clear</span>
          ) : (
            <span
              className={`badge ${open.some((a) => a.severity === 'critical') ? 'failed' : 'provisioning'}`}
            >
              {open.length} open
            </span>
          )}
        </h2>
        <span className="muted" style={{ fontSize: 12 }}>
          checked every 15 min · email + webhook on change
        </span>
      </div>
      {open.length > 0 && (
        <table>
          <tbody>
            {open.map((a) => (
              <tr key={a.id}>
                <td style={{ width: 90 }}>
                  <span className={`badge ${a.severity === 'critical' ? 'failed' : 'provisioning'}`}>
                    {a.severity}
                  </span>
                </td>
                <td>
                  <strong>{a.title}</strong>
                  <div className="muted">{a.detail}</div>
                </td>
                <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                  since {formatDate(a.firstSeenAt)}
                  <br />
                  notified {a.notifyCount}×
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {resolved.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="muted">Recently resolved ({resolved.length})</summary>
          <table>
            <tbody>
              {resolved.map((a) => (
                <tr key={a.id}>
                  <td>{a.title}</td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(a.firstSeenAt)} → {formatDate(a.resolvedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
